import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { z } from "zod";
import Swal from "sweetalert2";
import Footer from "../../../core/common/Footer";
import SearchableSelect from "../../../core/common/SearchableSelect";
import useApi from "../../../core/customhook/useApi";
import { useAuth } from "../../../core/context/useAuthContext";
import webservice_url from "../../../core/webservice/Webservice_url";

const AddAccount = () => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const [showGuide, setShowGuide] = useState<boolean>(true);

    const createAccount = useApi<any>(
      `/api/v1/master/organizations/${auth?.orgId}/projects`,
      {
        method: "POST",
        autoCall: false,
        handleGlobalError: false,
      },
    );

  const getCountries = useApi<any>(webservice_url?.countries, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });
  const getIntakeMethod = useApi<any>(webservice_url?.intake_method, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });
  const getCandidateApproval = useApi<any>(webservice_url?.candidate_approval, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });
  const getAccountTypeTire = useApi<any>(webservice_url?.account_type_tiers, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });
  const getIndustries = useApi<any>(webservice_url?.industries, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const createLoading = createAccount.loading;

  const [formData, setFormData] = useState({
    project_code: "",
    project_name: "",
    brand_name: "",
    legal_name: "",
    headquarter_address: "",
    headquarter_country: "",
    industry_id: "",
    accmanager_name: "",
    accmanager_email: "",

    intake_method_id: "",
    account_type_tier_id: "",
    candidate_approval_id: "",
    geography_id: "",
    // is_active: "",
    is_active: "true",
  });
  const [countries, setCountries] = useState<any[]>([]);
  const [intakeMethods, setIntakeMethods] = useState<any[]>([]);
  const [candidateApprovals, setCandidateApprovals] = useState<any[]>([]);
  const [accountTypeTiers, setAccountTypeTiers] = useState<any[]>([]);
  const [industries, setIndustries] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClearError = (field: string) => {
    setErrors((prev: any) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  };

  const requiredField = z.string().trim().min(1, "This field is required");

  const accountSchema = z.object({
    project_code: requiredField,
    project_name: requiredField,
    brand_name: requiredField,
    legal_name: requiredField,
    // headquarter_address: requiredField,
    // headquarter_country: requiredField,
    // industry_id: requiredField,
    // accmanager_name: requiredField,

    // accmanager_email: requiredField.email("Invalid email address"),

    intake_method_id: requiredField,
    // account_type_tier_id: requiredField,
    candidate_approval_id: requiredField,
    geography_id: requiredField,
    // is_active: requiredField,
  });

  const fetchDropdowns = async () => {
    try {
      const [
        countriesRes,
        intakeMethodRes,
        candidateApprovalRes,
        accountTypeTierRes,
        industriesRes,
      ] = await Promise.all([
        getCountries.callApi(),
        getIntakeMethod.callApi(),
        getCandidateApproval.callApi(),
        getAccountTypeTire.callApi(),
        getIndustries.callApi(),
      ]);
 
      const countriesData = countriesRes?.data || [];
      setCountries(countriesData);

      const india = countriesData.find(
        (item: any) => item.country_code === "IND",
      );

      if (india) {
        setFormData((prev) => ({
          ...prev,
          geography_id: prev.geography_id || india.country_id,
        }));
      } 
      const intakeMethodsData = intakeMethodRes?.data || [];
      setIntakeMethods(intakeMethodsData);

      const clientPortal = intakeMethodsData.find(
        (item: any) => item.intake_method_name === "Client Portal",
      );

      if (clientPortal) {
        setFormData((prev) => ({
          ...prev,
          intake_method_id:
            prev.intake_method_id || clientPortal.intake_method_id,
        }));
      } 
      const candidateApprovalsData = candidateApprovalRes?.data || [];
      setCandidateApprovals(candidateApprovalsData);

      const defaultApproval = candidateApprovalsData.find(
        (item: any) =>
          item.hr_approval ===
          "Form submitted by candidate will go directly to AupTech processing. Client HR does not need to approve cases",
      );

      if (defaultApproval) {
        setFormData((prev) => ({
          ...prev,
          candidate_approval_id:
            prev.candidate_approval_id || defaultApproval.candidate_approval_id,
        }));
      }
      setAccountTypeTiers(accountTypeTierRes?.data || []);
      setIndustries(industriesRes?.data || []);
    } catch (error) {
      console.error("Dropdown API Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load dropdown data.",
      });
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const hrApprovalOptions = candidateApprovals.map((item: any) => ({
    label: item.hr_approval,
    value: item.candidate_approval_id,
  }));

  const handleSubmit = async () => {
    const result = accountSchema.safeParse(formData);

    if (!result.success) {
      const validationErrors: Record<string, string> = {};

      result.error.issues.forEach((issue) => {
        validationErrors[issue.path[0] as string] = issue.message;
      });

      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      const res = await createAccount.callApi(formData);

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Account created successfully.",
          confirmButtonText: "OK",

          confirmButtonColor: "#f26522",
        });

        navigate(all_routes.newAccountSetup);
      }
    } catch (error: any) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong.",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Account Details</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item active">Add New Account</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        {/* <GuideContainer
          showGuide={showGuide}
          handleClick={() => setShowGuide((prev) => !prev)}
        /> */}

        <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
          <button
            className="btn border bg-white px-3"
            onClick={() => setShowGuide((prev) => !prev)}
          >
            <i className="ti ti-info-circle-filled fs-16"></i> Guide
          </button>
        </div>

        {showGuide && (
          <div
            style={{
              overflow: "hidden",
              maxHeight: showGuide ? "500px" : "0px",
              transition: "all 0.4s ease",
            }}
          >
            {/* Top Arrow */}
            <div className="text-end pe-3">
              <i
                className="ti ti-triangle-inverted-filled"
                style={{
                  fontSize: "22px",
                  color: "#cfcfcf",
                  lineHeight: 0,
                }}
              ></i>
            </div>

            {/* Guide Box */}
            <div
              className="card border-0 shadow-sm"
              style={{
                borderRadius: "4px",
              }}
            >
              <div className="card-body position-relative p-4">
                {/* Close Button */}
                <div className="position-absolute top-0 end-0 mt-3 me-3">
                  <i
                    className="ti ti-x"
                    style={{
                      background: "#6c757d",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowGuide(false)}
                  ></i>
                </div>

                {/* Heading */}
                <h5 className="fw-bold mb-3">Account Setup Instructions</h5>

                {/* Paragraph */}
                <p className="mb-3 text-muted" style={{ lineHeight: "1.7" }}>
                  Create a new client account by entering the required details and configuring its settings.
                </p>

                <ul
                  className="mb-0 ps-3 text-dark"
                  style={{ lineHeight: "1.8", listStyleType: "disc" }}
                >
                  <li>
                    Complete the Account Details and Account Configuration sections.
                  </li>
                  <li>
                    Click <b>"Create Account"</b> to save the new account.
                  </li>
                  <li>
                    To update an existing account, go to Account Management → Manage Account.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="card mt-3 shadow-sm border-0">
          <div className="card-body">
            <h4 className="fw-bold">Account Details</h4>
            <hr
              style={{
                border: 0,
                height: "1px",
                backgroundColor: "#d6d6d6",
                opacity: 1,
                marginBottom: "20px",
              }}
            />

            <div className="row gx-3 gy-4">
              {/* Row 1 */}
              <div className="col-md-4">
                <label className="form-label">
                  Account Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.project_code ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Account Code"
                  value={formData.project_code}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      project_code: e.target.value,
                    });
                    handleClearError("project_code");
                  }}
                />
                {errors.project_code && (
                  <div className="invalid-feedback">{errors.project_code}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Display Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.project_name ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Display name"
                  value={formData.project_name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      project_name: e.target.value,
                    });
                    handleClearError("project_name");
                  }}
                />
                {errors.project_name && (
                  <div className="invalid-feedback">{errors.project_name}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Brand Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Brand Name"
                  className={`form-control ${
                    errors.brand_name ? "is-invalid" : ""
                  }`}
                  value={formData.brand_name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      brand_name: e.target.value,
                    });
                    handleClearError("brand_name");
                  }}
                />

                {errors.brand_name && (
                  <div className="invalid-feedback">{errors.brand_name}</div>
                )}
              </div>

              {/* Row 2 */}
              <div className="col-md-4">
                <label className="form-label">
                  Legal Name <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter Legal Name"
                  className={`form-control ${
                    errors.legal_name ? "is-invalid" : ""
                  }`}
                  value={formData.legal_name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      legal_name: e.target.value,
                    });
                    handleClearError("legal_name");
                  }}
                />

                {errors.legal_name && (
                  <div className="invalid-feedback">{errors.legal_name}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Headquarters Address
                </label>
                <input
                  type="text"
                  placeholder="Enter Headquater Address"
                  className={`form-control ${
                    errors.headquarter_address ? "is-invalid" : ""
                  }`}
                  value={formData.headquarter_address}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      headquarter_address: e.target.value,
                    });
                    handleClearError("headquarter_address");
                  }}
                />

                {/* {errors.headquarter_address && (
                  <div className="invalid-feedback">
                    {errors.headquarter_address}
                  </div>
                )} */}
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Country 
                </label>
                <select
                  className={`form-select ${
                    errors.headquarter_country ? "is-invalid" : ""
                  }`}
                  value={formData.headquarter_country}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      headquarter_country: e.target.value,
                    });
                    handleClearError("headquarter_country");
                  }}
                >
                  <option value="">Select Country</option>
                  <option value="India">India</option>
                </select>
                {/* {errors.headquarter_country && (
                  <div className="invalid-feedback">
                    {errors.headquarter_country}
                  </div>
                )} */}
              </div>
              {/* Row 3 */}
              <div className="col-md-4">
                <label className="form-label">
                  Industry 
                </label>
                <select
                  className={`form-select ${
                    errors.industry_id ? "is-invalid" : ""
                  }`}
                  value={formData.industry_id}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      industry_id: e.target.value,
                    });
                    handleClearError("industry_id");
                  }}
                >
                  <option value="">Select Industry</option>
                  {industries.map((item: any) => (
                    <option key={item.industry_id} value={item.industry_id}>
                      {item.industry_name}
                    </option>
                  ))}
                </select>
                {/* {errors.industry_id && (
                  <div className="invalid-feedback">{errors.industry_id}</div>
                )} */}
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Account Manager Name  
                </label>
                <input
                  type="text"
                  placeholder="Enter Account Manager Name"
                  className={`form-control ${
                    errors.accmanager_name ? "is-invalid" : ""
                  }`}
                  value={formData.accmanager_name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      accmanager_name: e.target.value,
                    });
                    handleClearError("accmanager_name");
                  }}
                />

                {/* {errors.accmanager_name && (
                  <div className="invalid-feedback">
                    {errors.accmanager_name}
                  </div>
                )} */}
              </div>

              {/* Row 4 */}
              <div className="col-md-4">
                <label className="form-label">
                  Account Manager Email
                </label>
                <input
                  type="text"
                  placeholder="Enter Account Manager Email"
                  className={`form-control ${
                    errors.accmanager_email ? "is-invalid" : ""
                  }`}
                  value={formData.accmanager_email}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      accmanager_email: e.target.value,
                    });
                    handleClearError("accmanager_email");
                  }}
                />

                {/* {errors.accmanager_email && (
                  <div className="invalid-feedback">
                    {errors.accmanager_email}
                  </div>
                )} */}
              </div>
            </div>

            <h4 className="fw-bold mt-4">Account Configuration</h4>
            <hr
              style={{
                border: 0,
                height: "1px",
                backgroundColor: "#d6d6d6",
                opacity: 1,
                marginBottom: "20px",
              }}
            />

            <div className="row gx-3 gy-4">
              {/* Row 1 */}

              <div className="col-md-4">
                <label className="form-label">
                  Intake Method <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${
                    errors.intake_method_id ? "is-invalid" : ""
                  }`}
                  value={formData.intake_method_id}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      intake_method_id: e.target.value,
                    });
                    handleClearError("intake_method_id");
                  }}
                >
                  <option value="">Select Intake Method</option>
                  {intakeMethods.map((item: any) => (
                    <option
                      key={item.intake_method_id}
                      value={item.intake_method_id}
                    >
                      {item.intake_method_name}
                    </option>
                  ))}
                </select>
                {errors.intake_method_id && (
                  <div className="invalid-feedback">
                    {errors.intake_method_id}
                  </div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Account type via tier</label>
                <select
                  className={`form-select ${
                    errors.account_type_tier_id ? "is-invalid" : ""
                  }`}
                  value={formData.account_type_tier_id}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      account_type_tier_id: e.target.value,
                    });
                    handleClearError("account_type_tier_id");
                  }}
                >
                  <option>Select Account type tier</option>
                  {accountTypeTiers.map((item: any) => (
                    <option
                      key={item.account_type_tier_id}
                      value={item.account_type_tier_id}
                    >
                      {item.account_type_tier}
                    </option>
                  ))}
                </select>
                {errors.account_type_tier_id && (
                  <div className="invalid-feedback">
                    {errors.account_type_tier_id}
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <SearchableSelect
                  label="HR Approval"
                  name="riskMatrix"
                  value={formData.candidate_approval_id}
                  options={hrApprovalOptions}
                  onChange={(name, value) => {
                    setFormData({
                      ...formData,
                      candidate_approval_id: value,
                    });
                    handleClearError("candidate_approval_id");
                  }}
                  hasRowGrid={false}
                  error={errors.candidate_approval_id}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Geos <span className="text-danger">*</span>
                </label>

                <select
                  className={`form-select ${errors.geography_id ? "is-invalid" : ""}`}
                  value={formData.geography_id}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      geography_id: e.target.value,
                    });
                    handleClearError("geography_id");
                  }}
                >
                  <option value="">Select Geos</option>
                  {countries.map((item: any) => (
                    <option key={item.country_id} value={item.country_id}>
                      {item.country_name}
                    </option>
                  ))}
                </select>

                {errors.geography_id && (
                  <div className="invalid-feedback">{errors.geography_id}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Does Account Active</label>

                <select
                  className={`form-select ${errors.is_active ? "is-invalid" : ""}`}
                  value={formData.is_active}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      is_active: e.target.value,
                    });
                    handleClearError("is_active");
                  }}
                >
                  {/* <option value="">Select Active Status</option> */}
                  <option value="true">Yes</option>
                  {/* <option value="false">No</option> */}
                </select>

                {errors.is_active && (
                  <div className="invalid-feedback">{errors.is_active}</div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                {createLoading ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AddAccount;
